import { useState, useRef, useEffect } from 'react';

interface ShareWidgetProps {
  url: string;
  title: string;
  color: string;
}

interface ShareItem {
  icon: string;
  label: string;
  bg: string;
  accent: string;
  getHref: (url: string, title: string) => string;
}

const SHARE_ITEMS: ShareItem[] = [
  {
    icon: 'ri-telegram-line',
    label: 'Telegram',
    bg: 'rgba(42,171,238,0.08)',
    accent: '#2AABEE',
    getHref: (url, title) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  },
  {
    icon: 'ri-vk-line',
    label: 'ВКонтакте',
    bg: 'rgba(76,117,163,0.08)',
    accent: '#4C75A3',
    getHref: (url, title) => `https://vk.com/share.php?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
  },
  {
    icon: 'ri-whatsapp-line',
    label: 'WhatsApp',
    bg: 'rgba(37,211,102,0.08)',
    accent: '#25D366',
    getHref: (url, title) => `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`,
  },
  {
    icon: 'ri-twitter-x-line',
    label: 'X (Twitter)',
    bg: 'rgba(255,255,255,0.05)',
    accent: '#e7e9ea',
    getHref: (url, title) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  },
  {
    icon: 'ri-links-line',
    label: 'Скопировать ссылку',
    bg: 'rgba(255,255,255,0.05)',
    accent: '#ffffff',
    getHref: () => '',
  },
];

const ShareWidget = ({ url, title, color }: ShareWidgetProps) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [visibleItems, setVisibleItems] = useState<boolean[]>(new Array(SHARE_ITEMS.length).fill(false));
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Stagger items on open
  useEffect(() => {
    if (open) {
      setVisibleItems(new Array(SHARE_ITEMS.length).fill(false));
      SHARE_ITEMS.forEach((_, i) => {
        setTimeout(() => {
          setVisibleItems(prev => {
            const next = [...prev];
            next[i] = true;
            return next;
          });
        }, i * 55);
      });
    } else {
      setVisibleItems(new Array(SHARE_ITEMS.length).fill(false));
    }
  }, [open]);

  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Toggle button */}
      <button
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        aria-label="Поделиться статьёй"
        className="w-full flex items-center justify-between px-4 py-3 rounded-xl font-mono text-xs cursor-pointer transition-all duration-200 whitespace-nowrap"
        style={{
          background: open ? `${color}12` : 'rgba(255,255,255,0.03)',
          border: `1px solid ${open ? `${color}35` : 'rgba(255,255,255,0.08)'}`,
          color: open ? color : 'rgba(255,255,255,0.55)',
        }}
      >
        <span className="flex items-center gap-2">
          <i className="ri-share-forward-line" style={{ fontSize: '14px' }} />
          Поделиться
        </span>
        <i
          className="ri-arrow-down-s-line transition-transform duration-300"
          style={{
            fontSize: '16px',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>

      {/* Dropdown panel */}
      <div
        className="overflow-hidden transition-all duration-300"
        style={{
          maxHeight: open ? '320px' : '0px',
          opacity: open ? 1 : 0,
        }}
      >
        <div
          className="mt-2 rounded-xl p-3 flex flex-col gap-1"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          {SHARE_ITEMS.map((item, idx) => {
            const isCopyBtn = item.label === 'Скопировать ссылку';
            const isJustCopied = isCopyBtn && copied;

            return (
              <div
                key={item.label}
                style={{
                  opacity: visibleItems[idx] ? 1 : 0,
                  transform: visibleItems[idx] ? 'translateX(0)' : 'translateX(-10px)',
                  transition: `opacity 0.25s ease ${idx * 40}ms, transform 0.25s ease ${idx * 40}ms`,
                }}
              >
                {isCopyBtn ? (
                  <button
                    onClick={handleCopy}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-200 text-left"
                    style={{
                      background: isJustCopied ? 'rgba(57,255,20,0.08)' : 'transparent',
                      border: `1px solid ${isJustCopied ? 'rgba(57,255,20,0.2)' : 'transparent'}`,
                    }}
                    onMouseEnter={e => {
                      if (!isJustCopied) (e.currentTarget as HTMLElement).style.background = item.bg;
                    }}
                    onMouseLeave={e => {
                      if (!isJustCopied) (e.currentTarget as HTMLElement).style.background = 'transparent';
                    }}
                  >
                    <div
                      className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0"
                      style={{
                        background: isJustCopied ? 'rgba(57,255,20,0.12)' : item.bg,
                        border: `1px solid ${isJustCopied ? 'rgba(57,255,20,0.25)' : `${item.accent}20`}`,
                      }}
                    >
                      <i
                        className={isJustCopied ? 'ri-check-line' : item.icon}
                        style={{ color: isJustCopied ? '#39ff14' : item.accent, fontSize: '13px' }}
                      />
                    </div>
                    <span
                      className="font-mono text-xs"
                      style={{ color: isJustCopied ? '#39ff14' : 'rgba(255,255,255,0.55)' }}
                    >
                      {isJustCopied ? 'Скопировано!' : item.label}
                    </span>
                  </button>
                ) : (
                  <a
                    href={item.getHref(url, title)}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    aria-label={`Поделиться в ${item.label}`}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-200"
                    style={{ border: '1px solid transparent' }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.background = item.bg;
                      (e.currentTarget as HTMLElement).style.borderColor = `${item.accent}20`;
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.background = 'transparent';
                      (e.currentTarget as HTMLElement).style.borderColor = 'transparent';
                    }}
                  >
                    <div
                      className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0"
                      style={{ background: item.bg, border: `1px solid ${item.accent}20` }}
                    >
                      <i className={item.icon} style={{ color: item.accent, fontSize: '13px' }} />
                    </div>
                    <span className="font-mono text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>
                      {item.label}
                    </span>
                    <i className="ri-external-link-line ml-auto" style={{ color: 'rgba(255,255,255,0.15)', fontSize: '11px' }} />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ShareWidget;
